import { useApolloClient } from '@apollo/client';
import { QUERY_BULK_TRANSFER_TO_PARTNER_CHECK } from '~pages/dashboard/referral/transfer/queries';

const ValidateResultTemplate = {
  transferAmount: {
    success: true,
    errors: {},
  },
  bookingNumber: {
    success: true,
    errors: {},
  },
  currency: {
    success: true,
    errors: {},
  },
  partnerCode: {
    success: true,
    errors: {},
  },
  payoutStage: {
    success: true,
    errors: {},
  },
  passportFirstName: {
    success: true,
    errors: {},
  },
  passportLastName: {
    success: true,
    errors: {},
  },
};

function useTransferValidate() {
  const client = useApolloClient();

  const onSendRequest = async formData => {
    const { data: bulkTransferToPartnerCheckResponse } = await client.query({
      query: QUERY_BULK_TRANSFER_TO_PARTNER_CHECK,
      variables: {
        input: formData.map(item => ({
          amount: item.transferAmount,
          bookingNumber: item.bookingNumber,
          currency: item.currency,
          partnerCode: item.partnerCode,
          payoutStage: item.payoutStage,
          passportFirstName: item.passportFirstName,
          passportLastName: item.passportLastName,
        })),
      },
      fetchPolicy: 'no-cache',
    });
    return bulkTransferToPartnerCheckResponse;
  };

  const validateTransfer = async (type, data) => {
    let validateResult = {};
    if (type === 'form') {
      validateResult = generateValidateResultFromForm(data);
    }
    if (type === 'response') {
      const response = await onSendRequest(data);
      validateResult = generateValidateResultFromResponse(response);
    }

    const isAllValid = Object.keys(validateResult).every(key => validateResult[key].success);

    return {
      isAllValid,
      validateResult,
    };
  };

  return { validateTransfer };
}

function generateValidateResultFromForm(formData) {
  const validateResult = structuredClone(ValidateResultTemplate);

  const initError = (key, errorName) => {
    if (!validateResult[key].errors[errorName]) {
      validateResult[key].errors[errorName] = {
        errorMessage: '',
        effectItems: [],
      };
    }
  };
  formData.forEach((item, index) => {
    if (!item.transferAmount) {
      validateResult.transferAmount.success = false;
      initError('transferAmount', 'required');
      validateResult.transferAmount.errors.required.errorMessage =
        'cms.referral.transfer.editor.error.required.transfer_amount';
      validateResult.transferAmount.errors.required.effectItems.push(
        item.partnerCode || `Line ${index + 1}`,
      );
    }
    if (!item.bookingNumber) {
      validateResult.bookingNumber.success = false;
      initError('bookingNumber', 'required');
      validateResult.bookingNumber.errors.required.errorMessage =
        'cms.referral.transfer.editor.error.required.booking_number';
      validateResult.bookingNumber.errors.required.effectItems.push(
        item.partnerCode || `Line ${index + 1}`,
      );
    }
    if (!item.currency) {
      validateResult.currency.success = false;
      initError('currency', 'required');
      validateResult.currency.errors.required.errorMessage =
        'cms.referral.transfer.editor.error.required.currency';
      validateResult.currency.errors.required.effectItems.push(
        item.partnerCode || `Line ${index + 1}`,
      );
    }
    if (!item.partnerCode) {
      validateResult.partnerCode.success = false;
      initError('partnerCode', 'required');
      validateResult.partnerCode.errors.required.errorMessage =
        'cms.referral.transfer.editor.error.required.partner_code';
      validateResult.partnerCode.errors.required.effectItems.push(
        item.partnerCode || `${index + 1} Line`,
      );
    }
    if (!item.payoutStage) {
      validateResult.payoutStage.success = false;
      initError('payoutStage', 'required');
      validateResult.payoutStage.errors.required.errorMessage =
        'cms.referral.transfer.editor.error.required.payout_stage';
      validateResult.payoutStage.errors.required.effectItems.push(
        item.partnerCode || `Line ${index + 1}`,
      );
    }
    if (!item.passportFirstName) {
      validateResult.passportFirstName.success = false;
      initError('passportFirstName', 'required');
      validateResult.passportFirstName.errors.required.errorMessage =
        'cms.referral.transfer.editor.error.required.passport_first_name';
      validateResult.passportFirstName.errors.required.effectItems.push(
        item.partnerCode || `Line ${index + 1}`,
      );
    }
    if (!item.passportLastName) {
      validateResult.passportLastName.success = false;
      initError('passportLastName', 'required');
      validateResult.passportLastName.errors.required.errorMessage =
        'cms.referral.transfer.editor.error.required.passport_last_name';
      validateResult.passportLastName.errors.required.effectItems.push(
        item.partnerCode || `Line ${index + 1}`,
      );
    }
  });

  return validateResult;
}

function generateValidateResultFromResponse(response) {
  const validateResult = structuredClone(ValidateResultTemplate);

  const initError = (key, errorName) => {
    if (!validateResult[key].errors[errorName]) {
      validateResult[key].errors[errorName] = {
        errorMessage: '',
        effectItems: [],
      };
    }
  };

  //Input Duplication Error
  const inputDataDuplication = response?.bulkTransferToPartnerCheck.inputDataDuplication;

  //Booking Number Error
  const bookingNumberExistence = response?.bulkTransferToPartnerCheck.bookingNumberExistence;
  const bookingNumberWithPartnerCodeExistence =
    response?.bulkTransferToPartnerCheck.bookingNumberWithPartnerCodeExistence;

  //Partner Code Error
  const partnerExistence = response?.bulkTransferToPartnerCheck.partnerExistence;
  const partnerBindCountry = response?.bulkTransferToPartnerCheck.partnerBindCountry;
  const partnerCountryExistence = response?.bulkTransferToPartnerCheck.partnerCountryExistence;

  //Payout Stage Error
  const inputDataDuplicationWithDB =
    response?.bulkTransferToPartnerCheck.inputDataDuplicationWithDB;

  //Currency Error
  const supportedCurrency = response?.bulkTransferToPartnerCheck.supportedCurrency;
  const bookingNumberWithCurrency = response?.bulkTransferToPartnerCheck.bookingNumberWithCurrency;

  //Passport Name Error
  const bookingBindMutilStudent = response?.bulkTransferToPartnerCheck.bookingBindMutilStudent;
  const bookingNumberBindStudentCorrectly =
    response?.bulkTransferToPartnerCheck.bookingNumberBindStudentCorrectly;

  //Transfer Amount Error
  //Balance Error
  const companyWalletBalance = response?.bulkTransferToPartnerCheck.companyWalletBalance;

  //Booking Number Error
  if (!inputDataDuplication?.success && bookingNumberExistence?.checked) {
    validateResult.bookingNumber.success = false;
    initError('bookingNumber', 'exist');
    validateResult.bookingNumber.errors.exist.errorMessage =
      'cms.referral.transfer.editor.error.exist.existed';
    validateResult.bookingNumber.errors.exist.effectItems =
      inputDataDuplication?.error?.extensions.data.map(item => item.bookingNumber) || [];
  }
  if (!bookingNumberExistence?.success && bookingNumberExistence.checked) {
    validateResult.bookingNumber.success = false;
    initError('bookingNumber', 'exist');
    validateResult.bookingNumber.errors.exist.errorMessage =
      'cms.referral.transfer.editor.error.exist.not_exist';
    validateResult.bookingNumber.errors.exist.effectItems =
      bookingNumberExistence?.error?.extensions.data.map(item => item.bookingNumber) || [];
  }

  if (
    !bookingNumberWithPartnerCodeExistence?.success &&
    bookingNumberWithPartnerCodeExistence.checked
  ) {
    validateResult.bookingNumber.success = false;
    initError('bookingNumber', 'exist');
    validateResult.bookingNumber.errors.exist.errorMessage =
      'cms.referral.transfer.editor.error.exist.partner_code';
    validateResult.bookingNumber.errors.exist.effectItems =
      bookingNumberWithPartnerCodeExistence?.error?.extensions.data.map(
        item => item.bookingNumber,
      ) || [];
  }

  //Partner Code Error
  if (!partnerExistence?.success && partnerExistence?.checked) {
    validateResult.partnerCode.success = false;
    initError('partnerCode', 'exist');
    validateResult.partnerCode.errors.exist.errorMessage =
      'cms.referral.transfer.editor.error.exist.not_exist';
    validateResult.partnerCode.errors.exist.effectItems =
      partnerExistence?.error?.extensions.data.map(item => item.partnerCode) || [];
  }
  if (!partnerBindCountry?.success && partnerBindCountry?.checked) {
    validateResult.partnerCode.success = false;
    initError('partnerCode', 'noCountry');
    validateResult.partnerCode.errors.noCountry.errorMessage =
      'cms.referral.transfer.editor.error.no_country.partner_code';
    validateResult.partnerCode.errors.noCountry.effectItems =
      partnerBindCountry?.error?.extensions.data.map(item => item.partnerCode) || [];
  }
  if (!partnerCountryExistence?.success && partnerCountryExistence?.checked) {
    validateResult.partnerCode.success = false;
    initError('partnerCode', 'noCountry');
    validateResult.partnerCode.errors.noCountry.errorMessage =
      'cms.referral.transfer.editor.error.no_country.country_not_exist.partner_code';
    validateResult.partnerCode.errors.noCountry.effectItems =
      partnerCountryExistence?.error?.extensions.data.map(item => item.countryCode) || [];
  }

  //Currency Error
  if (!supportedCurrency?.success && supportedCurrency?.checked) {
    validateResult.currency.success = false;
    initError('currency', 'exist');
    validateResult.currency.errors.exist.errorMessage =
      'cms.referral.transfer.editor.error.exist.not_exist.currency';
    validateResult.currency.errors.exist.effectItems =
      supportedCurrency?.error?.extensions.data.map(item => item.currency) || [];
  }
  if (!bookingNumberWithCurrency?.success && bookingNumberWithCurrency?.checked) {
    validateResult.currency.success = false;
    initError('currency', 'alreadyBind');
    validateResult.currency.errors.alreadyBind.errorMessage =
      'cms.referral.transfer.editor.error.exist.existed.currency';
    validateResult.currency.errors.alreadyBind.effectItems =
      bookingNumberWithCurrency?.error?.extensions.data.map(item => item.bookingNumber) || [];
  }

  //Passport Name Error
  if (!bookingBindMutilStudent?.success && bookingBindMutilStudent?.checked) {
    validateResult.bookingNumber.success = false;
    initError('bookingNumber', 'passportName');
    validateResult.bookingNumber.errors.passportName.errorMessage =
      'cms.referral.transfer.editor.error.exist.existed.booking_number';
    validateResult.bookingNumber.errors.passportName.effectItems =
      bookingBindMutilStudent?.error?.extensions.data.map(item => item.bookingNumber) || [];
  }
  if (!bookingNumberBindStudentCorrectly?.success && bookingNumberBindStudentCorrectly?.checked) {
    validateResult.bookingNumber.success = false;
    initError('bookingNumber', 'passportName');
    validateResult.bookingNumber.errors.passportName.errorMessage =
      'cms.referral.transfer.editor.error.not_correct.booking_number';
    validateResult.bookingNumber.errors.passportName.effectItems =
      bookingNumberBindStudentCorrectly?.error?.extensions.data.map(item => item.bookingNumber) ||
      [];
  }

  //Balance Error
  if (!companyWalletBalance?.success && companyWalletBalance?.checked) {
    validateResult.transferAmount.success = false;
    initError('transferAmount', 'insufficientBalance');
    validateResult.transferAmount.errors.insufficientBalance.errorMessage =
      'cms.referral.transfer.editor.error.insufficient_balance.transfer_amount';
    validateResult.transferAmount.errors.insufficientBalance.effectItems =
      companyWalletBalance?.error?.extensions.data.map(item => item.currency) || [];
  }

  //Payout Stage Error
  if (!inputDataDuplicationWithDB?.success && inputDataDuplicationWithDB?.checked) {
    validateResult.payoutStage.success = false;
    initError('payoutStage', 'insufficientBalance');
    validateResult.payoutStage.errors.insufficientBalance.errorMessage =
      'cms.referral.transfer.editor.error.exist.existed.payout_stage';
    validateResult.payoutStage.errors.insufficientBalance.effectItems =
      inputDataDuplicationWithDB?.error?.extensions.data.map(item => item.bookingNumber) || [];
  }

  return validateResult;
}

export default useTransferValidate;
